import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import type {
  MeetingCorrectionScope,
  MeetingKind,
  MeetingThreadItem,
} from '@wundo/shared/meetingPage.ts';
import { meetingAlwaysLabel } from './meetingAlwaysLabel.ts';
import { m } from '@/paraglide/messages.js';

export interface ScopeDialogProps {
  open: boolean;
  /** The thread the "always" answer would land on, when there already is one. */
  thread: MeetingThreadItem | null;
  /** What Wundo takes this meeting to be, for a meeting with no thread yet. */
  kind: MeetingKind;
  /**
   * Whether this is an event Wundo never picks up on its own. Only a choice for
   * this one sitting reaches it, so there is no question left to ask.
   */
  occurrenceOnly: boolean;
  failed: boolean;
  pending: boolean;
  onChoose: (scope: MeetingCorrectionScope) => void;
  onCancel: () => void;
}

/**
 * The one question every flip asks: this one time, or from now on?
 *
 * There is no third answer. A correction points at one occurrence or at the
 * thread, and "always" is named after whichever of the two the thread is — all
 * conversations with a person, or this meeting every time — so the user reads
 * what they are about to change rather than the word "always".
 *
 * On an event nobody attends or one with a crowd there is no question at all:
 * a standing choice would be stored and then ignored, so offering it would be a
 * lie. The dialog says what it can do instead of asking.
 */
export function ScopeDialog({
  open,
  thread,
  kind,
  occurrenceOnly,
  failed,
  pending,
  onChoose,
  onCancel,
}: ScopeDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>
            {occurrenceOnly ? m.meeting_scope_once_only_title() : m.meeting_scope_title()}
          </DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-2.5 px-5 py-4'>
          {occurrenceOnly && (
            <p className='text-sm text-ink-body'>{m.meeting_scope_once_only_body()}</p>
          )}

          <Button
            shape='block'
            variant={occurrenceOnly ? 'primary' : 'quiet'}
            fullWidth
            disabled={pending}
            onClick={() => onChoose('occurrence')}
          >
            {m.meeting_scope_once()}
          </Button>
          {!occurrenceOnly && (
            <Button
              shape='block'
              fullWidth
              disabled={pending}
              onClick={() => onChoose('always')}
            >
              {meetingAlwaysLabel(thread, kind)}
            </Button>
          )}

          {failed && (
            <p role='alert' className='text-sm text-danger'>
              {m.meeting_action_failed()}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={onCancel}>
            {m.meeting_cancel()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
